import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { FilterBar, type FilterSpec } from "@/components/data";

import { getFilters, STALE_TIME, type FilterNarrowing } from "./api";
import {
  onFilterChange,
  onOverviewFilterChange,
  readLastItem,
  readShared,
  SHARED,
  writeLastItem,
  type SharedName,
} from "./filters";
import { ItemLink } from "./links";
import { frameContext } from "./PageFrame";
import type { SourcingTab } from "./routes";

export const FILTERS_UNAVAILABLE = "Filter options unavailable. Try again shortly.";

const FILTER_LABELS: Record<SharedName, string> = {
  item: "Item",
  funnel: "Funnel",
  phase: "Phase",
  option: "Option",
  geo: "Geo",
  dropped: "Dropped",
};

export function notApplyingNote(names: SharedName[]): string {
  const labels = names.map((name) => FILTER_LABELS[name]);
  const list = labels.length > 1 ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}` : labels[0];
  return `${list} ${labels.length > 1 ? "don't" : "doesn't"} apply to this page.`;
}

export function useFilterOptions(item: string | null, enabled = true, narrow: FilterNarrowing = {}) {
  const key = { funnel: narrow.funnel ?? null, phase: narrow.phase ?? null, geo: narrow.geo ?? null };
  const narrowed = key.funnel !== null || key.phase !== null || key.geo !== null;
  return useQuery({
    // An un-narrowed call keeps the old key, so the module head and other pages share its cache.
    queryKey: narrowed ? ["sourcing_funnel", "filters", item, key] : ["sourcing_funnel", "filters", item],
    queryFn: () => getFilters(item, key),
    // A changed Funnel/Phase/Geo keeps the last lists on screen while the narrowed ones load, so no pill empties.
    // A changed item does not: the last item's options must never be offered (or picked) under the new item.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === item ? keepPreviousData(previous) : undefined,
    staleTime: STALE_TIME,
    enabled,
  });
}

/** Whether an item sits in a Funnel choice: an item TYPE (Tech Stack…) or, for old links, a dataset name (as the API matches). */
export function inFunnel(item: { funnel: string; type?: string | null } | undefined, funnel: string): boolean {
  return item !== undefined && (item.funnel === funnel || item.type === funnel);
}

/** A list with the current choice kept in it, so a narrowed list never hides what is chosen. */
export function keepChosen(values: string[], chosen: string | null): string[] {
  return chosen && !values.includes(chosen) ? [...values, chosen] : values;
}

export function withChosen(
  options: { value: string; label: string }[],
  chosen: string | null,
  label: string | undefined,
): { value: string; label: string }[] {
  if (!chosen || options.some((option) => option.value === chosen)) return options;
  return [...options, { value: chosen, label: label ?? chosen }];
}

/** Dataset names (old links, row labels) and the item TYPEs the Funnel filter now lists; others show as sent. */
export const FUNNEL_LABELS: Record<string, string> = {
  services: "Services",
  ingredients: "Ingredients",
  packaging: "Packaging",
  OTHER: "Other",
};

/** The Funnel pill's choices: every item TYPE the API sends, plus the current choice, with readable labels. */
export function funnelOptions(funnels: string[], chosen: string | null): { value: string; label: string }[] {
  return keepChosen(funnels, chosen).map((funnel) => ({ value: funnel, label: FUNNEL_LABELS[funnel] ?? funnel }));
}

export function SharedFilterBar({ tab }: { tab: SourcingTab }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const shared = readShared(search);
  // The lists follow each other: each is narrowed by the other choices (the API does the narrowing).
  // Only a filter this page applies narrows the lists: a "doesn't apply" pill must not hide choices.
  const applied = (name: "funnel" | "phase" | "geo") => (tab.accepts.includes(name) ? shared[name] : null);
  const options = useFilterOptions(shared.item, true, { funnel: applied("funnel"), phase: applied("phase"), geo: applied("geo") });
  // The un-narrowed lists name a choice the narrowed ones no longer hold, so a pill never shows a blank.
  const full = useFilterOptions(shared.item).data?.data ?? null;
  const data = options.data?.data ?? null;
  const unavailable = options.isError || (options.isSuccess && data === null);

  // Option Overview is about one option: its pill shows that option, whether a link or the filter chose it.
  const viewing = tab.needs === "option" ? frameContext(search).option : shared.option;
  const change = (previous: Record<string, unknown>, next: Record<string, string | string[] | null>) =>
    tab.needs === "option" ? onOverviewFilterChange(previous, next, viewing) : onFilterChange(previous, next);

  const values: Record<string, string | null> = { ...shared, option: viewing };
  const setFilters = (input: Record<string, string | string[] | null>) => {
    const next = { ...input };
    // A new Funnel is the widest cut: an item (and its option) chosen under the old one would no longer belong to it.
    // An item already in the new Funnel stays; one not found in the un-narrowed list is dropped to be safe.
    const chosen = (full?.items ?? []).find((item) => item.item_id === values.item);
    if (tab.needs !== "option" && typeof next.funnel === "string" && next.funnel !== values.funnel && values.item !== null && !inFunnel(chosen, next.funnel)) {
      next.item = null;
      next.option = null;
    }
    // Choosing "All" forgets the item; a chosen item is remembered by the effect below.
    if (shared.item !== null && readShared(change(search, next)).item === null) writeLastItem(null);
    void navigate({
      search: ((previous: Record<string, unknown>) => change(previous, next)) as never,
      replace: false,
    } as never);
  };

  // A link or a shared URL changes the item without touching the bar: remember it from the URL.
  useEffect(() => {
    if (shared.item) writeLastItem(shared.item);
  }, [shared.item]);

  const items = data?.items ?? [];
  const restored = useRef(false);
  useEffect(() => {
    // The un-narrowed list decides: a Funnel choice must not make the remembered item look missing.
    const every = full?.items ?? [];
    if (restored.current || every.length === 0) return;
    restored.current = true;
    if (shared.item) return;
    const last = readLastItem();
    // It must also sit in the chosen Funnel, or restoring it would contradict that choice. The un-narrowed
    // list carries each item's funnel and type, so the check never waits on (or races) the narrowed list.
    const remembered = every.find((item) => item.item_id === last);
    const funnel = tab.accepts.includes("funnel") ? shared.funnel : null;
    if (last && remembered && (funnel === null || inFunnel(remembered, funnel))) {
      void navigate({
        search: ((previous: Record<string, unknown>) => onFilterChange(previous, { item: last })) as never,
        replace: true,
      } as never);
    }
  }, [shared.item, shared.funnel, tab.accepts, full, navigate]);

  const notApplying = SHARED.filter(
    (name) => !tab.accepts.includes(name)
      && !(name === "dropped" && tab.ignoresDropped)
      && !(name === "phase" && tab.criteriaPhasePill),
  );
  const geoValues = keepChosen(Array.from(new Set((data?.geo ?? []).map((geo) => geo.value))), shared.geo);
  const off = (name: SharedName) => unavailable || !tab.accepts.includes(name);
  const allSpecs: FilterSpec[] = [
    // Funnel first: it is the widest cut, so it reads left of the item it narrows to.
    {
      name: "funnel",
      label: "Funnel",
      kind: "choice",
      anyLabel: "All",
      disabled: off("funnel"),
      options: funnelOptions(data?.funnels ?? [], shared.funnel),
    },
    {
      name: "item",
      label: "Item",
      kind: "choice",
      anyLabel: "All",
      disabled: off("item"),
      options: withChosen(
        items.map((item) => ({ value: item.item_id, label: item.item_name })),
        shared.item,
        (full?.items ?? []).find((item) => item.item_id === shared.item)?.item_name,
      ),
    },
    {
      name: "phase",
      label: "Phase",
      kind: "choice",
      anyLabel: "All",
      disabled: off("phase"),
      options: keepChosen(data?.phases ?? [], shared.phase).map((phase) => ({ value: phase, label: phase })),
    },
    {
      name: "option",
      label: "Option",
      kind: "choice",
      anyLabel: "All",
      disabled: off("option") || !shared.item,
      options: withChosen(
        (data?.options ?? []).map((option) => ({ value: option.option_id, label: option.option_name })),
        viewing,
        (full?.options ?? []).find((option) => option.option_id === viewing)?.option_name,
      ),
    },
    {
      name: "geo",
      label: "Geo",
      kind: "choice",
      anyLabel: "All",
      disabled: off("geo"),
      options: geoValues.map((value) => ({ value, label: value })),
    },
    {
      name: "dropped",
      label: "Dropped",
      kind: "choice",
      anyLabel: "Hide",
      disabled: off("dropped"),
      options: [
        { value: "show", label: "Show" },
        { value: "only", label: "Only dropped" },
      ],
    },
  ];
  const specs = allSpecs.filter((spec) => !(tab.criteriaPhasePill && spec.name === "phase"));

  const trailing =
    shared.item && tab.tabKey !== "item_overview" ? (
      <ItemLink itemId={shared.item} from={tab.tabKey}>Open item →</ItemLink>
    ) : null;

  return (
    <div className="flex flex-col gap-2">
      <FilterBar
        filters={specs}
        values={values}
        onChange={setFilters}
        layout="inline"
        searchable
        trailing={trailing}
      />
      {unavailable && <p role="alert">{FILTERS_UNAVAILABLE}</p>}
      {tab.ignoresDropped && <p className="text-sm text-ks-grey-1">Shows dropped options by design.</p>}
      {tab.note && <p className="text-sm text-ks-grey-1">{tab.note}</p>}
      {notApplying.length > 0 && <p className="text-sm text-ks-grey-1">{notApplyingNote(notApplying)}</p>}
    </div>
  );
}
